import { useEffect, useRef, useState } from "preact/hooks";
import type { PendingPayment } from "../../features/checkout/checkoutTypes";
import type { PaymentAmountSelection } from "../../features/checkout/amountSelection";
import {
  createFinixPendingPayment,
  finixCardTokenFormOptions,
  finixPaymentFormId,
  finixScriptUrl,
} from "../../features/checkout/providers/finixProvider";
import type { FinixForm, FinixFormOptions } from "../../features/checkout/providers/finixProvider";
import { useExternalScript } from "../../lib/externalAssets";
import { formatMoney } from "../../lib/format";
import type { DispatchProPaymentSummary } from "../../features/checkout/payment";
import { getApiFailureMessage, useCreateTemporaryCardMutation } from "../../store/gboApi";

type Props = {
  customerId: string;
  invoiceId?: string;
  payment: DispatchProPaymentSummary;
  paymentAmount: PaymentAmountSelection;
  onBack: () => void;
  onPaymentReady: (payment: PendingPayment) => void;
};

declare global {
  interface Window {
    Finix?: {
      Auth: (
        environment: "sandbox" | "live",
        merchantId: string,
        callback: (sessionKey: string) => void,
      ) => void;
      CardTokenForm: (formId: string, options?: FinixFormOptions) => FinixForm;
    };
  }
}

export function FinixPaymentPanel({
  customerId,
  invoiceId,
  payment,
  paymentAmount,
  onBack,
  onPaymentReady,
}: Props) {
  const scriptStatus = useExternalScript(finixScriptUrl);
  const [createTemporaryCard] = useCreateTemporaryCardMutation();
  const [formHasErrors, setFormHasErrors] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finixForm = useRef<FinixForm | null>(null);

  useEffect(() => {
    if (scriptStatus !== "ready" || !window.Finix) return;

    window.Finix.Auth(payment.environment, payment.merchantId, setSessionId);
  }, [payment.environment, payment.merchantId, scriptStatus]);

  useEffect(() => {
    if (scriptStatus !== "ready" || !window.Finix || finixForm.current) return;

    finixForm.current = window.Finix.CardTokenForm(finixPaymentFormId, {
      ...finixCardTokenFormOptions,
      onUpdate(_state, _binInformation, formHasErrors) {
        setFormHasErrors(formHasErrors);
      },
    });
  }, [scriptStatus]);

  function onSubmit(event: Event) {
    event.preventDefault();
    setError(null);

    if (formHasErrors) {
      setError("Please correct the errors on the form.");
      return;
    }

    if (paymentAmount.customAmountError) {
      setError(paymentAmount.customAmountError);
      return;
    }

    if (!finixForm.current || !sessionId) return;

    setSubmitting(true);
    finixForm.current.submit(
      payment.environment,
      payment.applicationId,
      async (submitError, response) => {
        const token = response?.data?.id;
        if (submitError || !token) {
          setSubmitting(false);
          setError("Payment information could not be validated.");
          return;
        }

        try {
          const tempCard = await createTemporaryCard({ request: { customerId, token } }).unwrap();
          if (tempCard.processor !== "DispatchPro")
            throw new Error("The payment provider returned an invalid card.");

          onPaymentReady(
            createFinixPendingPayment({
              customerId,
              fraudSessionId: sessionId,
              invoiceId,
              payment,
              paymentAmount,
              tempCard,
            }),
          );
        } catch (error) {
          setError(getApiFailureMessage(error, "Unable to submit payment."));
        } finally {
          setSubmitting(false);
        }
      },
    );
  }

  return (
    <section class="payment-panel" aria-label="Payment">
      {scriptStatus === "error" ? (
        <div class="payment-provider-shell" data-provider={payment.provider}>
          <p role="alert">
            Payment options could not be loaded. Refresh the page or try again later.
          </p>
        </div>
      ) : (
        <form class="finix-form" onSubmit={onSubmit}>
          <div class="payment-step-summary">
            <span>Payment amount</span>
            <strong>{formatMoney(paymentAmount.amount)}</strong>
            <button class="link-action" type="button" onClick={onBack} disabled={submitting}>
              Change
            </button>
          </div>

          <div id={finixPaymentFormId}></div>

          {payment.surchargePercentage != null && payment.surchargePercentage > 0 && (
            <p class="payment-note">
              Credit card payments may include a {payment.surchargePercentage}% surcharge. Debit and
              prepaid cards are not surcharged.
            </p>
          )}

          {error && (
            <p class="payment-error" role="alert">
              {error}
            </p>
          )}

          <button
            class="primary-action"
            type="submit"
            disabled={
              scriptStatus !== "ready" ||
              submitting ||
              !sessionId ||
              Boolean(paymentAmount.customAmountError)
            }
          >
            {submitting ? "Submitting..." : `Pay ${formatMoney(paymentAmount.amount)}`}
          </button>
        </form>
      )}
    </section>
  );
}
